import type { CatalogInvestigationRow, TaxonomyNode } from '@/modules/lab/catalog/types'

const CATEGORY_LABELS: Record<string, string> = {
  HEMATOLOGY: 'Haematology',
  BIOCHEMISTRY: 'Biochemistry',
  MICROBIOLOGY: 'Microbiology',
  SEROLOGY: 'Serology',
  IMMUNOLOGY: 'Immunology',
  HISTOPATHOLOGY: 'Histopathology',
  URINE_EXAMINATION: 'Urine Examination',
  CLINICAL_PATHOLOGY: 'Clinical Pathology',
  GENERAL: 'General',
  PATHOLOGY: 'Pathology',
}

export function categoryLabel(code: string): string {
  return CATEGORY_LABELS[code.toUpperCase()] ?? code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function buildTaxonomyTree(rows: CatalogInvestigationRow[]): TaxonomyNode[] {
  const counts = new Map<string, { total: number; enabled: number }>()
  for (const row of rows) {
    const key = row.category?.trim().toUpperCase() || 'GENERAL'
    const cur = counts.get(key) ?? { total: 0, enabled: 0 }
    cur.total += 1
    if (row.hospitalEnabled) cur.enabled += 1
    counts.set(key, cur)
  }

  const categories = [...counts.entries()]
    .sort(([a], [b]) => categoryLabel(a).localeCompare(categoryLabel(b)))
    .map(([code, c]) => ({
      id: `cat:${code}`,
      label: categoryLabel(code),
      kind: 'category' as const,
      count: c.total,
      enabledCount: c.enabled,
    }))

  const enabledAll = rows.filter((r) => r.hospitalEnabled).length
  return [
    {
      id: 'all',
      label: 'All investigations',
      kind: 'root',
      count: rows.length,
      enabledCount: enabledAll,
      children: categories,
    },
  ]
}

export function filterRowsByTaxonomy(
  rows: CatalogInvestigationRow[],
  selectedNodeId: string | null,
): CatalogInvestigationRow[] {
  if (!selectedNodeId || selectedNodeId === 'all') return rows
  if (!selectedNodeId.startsWith('cat:')) return rows
  const cat = selectedNodeId.slice(4)
  return rows.filter((r) => (r.category?.trim().toUpperCase() || 'GENERAL') === cat)
}

export function filterRowsByQuery(rows: CatalogInvestigationRow[], q: string): CatalogInvestigationRow[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter((r) => {
    const hay = [
      r.investigationCode,
      r.displayName,
      r.localCode ?? '',
      r.localDisplayName ?? '',
      r.category,
      ...r.aliases,
    ]
      .join(' ')
      .toLowerCase()
    return hay.includes(needle)
  })
}
